import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal, ClipboardList } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user.branch_id || "");
  const [bs, setBs] = useState(null);
  const [kiosk, setKiosk] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); if (!branchId && r.data[0]) setBranchId(r.data[0].id); }).catch(() => {});
    if (user.role !== "SUPER_ADMIN") api.get("/kiosk-settings").then((r) => setKiosk(r.data.fields));
    else setKiosk([]);
  }, []);
  useEffect(() => { if (branchId) api.get(`/booking-settings/${branchId}`).then((r) => setBs(r.data)).catch((e) => toast.error(apiError(e))); }, [branchId]);

  const saveBs = async () => {
    setSaving(true);
    try {
      await api.put(`/booking-settings/${branchId}`, {
        working_hours: bs.working_hours, free_duration_minutes: Number(bs.free_duration_minutes),
        min_duration_minutes: Number(bs.min_duration_minutes), max_duration_minutes: Number(bs.max_duration_minutes),
        advance_days: Number(bs.advance_days), approval_required_for_extended: bs.approval_required_for_extended,
      });
      toast.success("Booking configuration saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const saveKiosk = async () => {
    try { await api.put("/kiosk-settings", { fields: kiosk }); toast.success("Kiosk fields saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Booking rules and visitor kiosk configuration." />
      <Tabs defaultValue="booking">
        <TabsList className="mb-5">
          <TabsTrigger value="booking" data-testid="settings-tab-booking"><SlidersHorizontal size={15} className="mr-1.5" /> Booking Config</TabsTrigger>
          {user.role !== "SUPER_ADMIN" && <TabsTrigger value="kiosk" data-testid="settings-tab-kiosk"><ClipboardList size={15} className="mr-1.5" /> Visitor Kiosk</TabsTrigger>}
        </TabsList>

        <TabsContent value="booking">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
            {user.role !== "BRANCH_ADMIN" && user.role !== "RECEPTIONIST" && (
              <div className="mb-4">
                <Label>Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="mt-1 max-w-xs" data-testid="settings-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {!bs ? <Loader /> : (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Working / Booking Hours</Label><Input data-testid="bs-hours" className="mt-1 h-10" value={bs.working_hours} onChange={(e) => setBs({ ...bs, working_hours: e.target.value })} /></div>
                  <div><Label>Free Booking Duration (min)</Label><Input type="number" data-testid="bs-free" className="mt-1 h-10" value={bs.free_duration_minutes} onChange={(e) => setBs({ ...bs, free_duration_minutes: e.target.value })} /></div>
                  <div><Label>Minimum Duration (min)</Label><Input type="number" data-testid="bs-min" className="mt-1 h-10" value={bs.min_duration_minutes} onChange={(e) => setBs({ ...bs, min_duration_minutes: e.target.value })} /></div>
                  <div><Label>Maximum Duration (min)</Label><Input type="number" data-testid="bs-max" className="mt-1 h-10" value={bs.max_duration_minutes} onChange={(e) => setBs({ ...bs, max_duration_minutes: e.target.value })} /></div>
                  <div><Label>Advance Booking Window (days)</Label><Input type="number" data-testid="bs-advance" className="mt-1 h-10" value={bs.advance_days} onChange={(e) => setBs({ ...bs, advance_days: e.target.value })} /></div>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-slate-100">
                  <span className="text-sm font-medium text-navy-900">Approval required for extended bookings</span>
                  <Switch checked={bs.approval_required_for_extended} data-testid="bs-approval" onCheckedChange={(v) => setBs({ ...bs, approval_required_for_extended: v })} />
                </div>
                <Button onClick={saveBs} disabled={saving} data-testid="save-booking-settings" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Configuration"}</Button>
              </div>
            )}
          </div>
        </TabsContent>

        {user.role !== "SUPER_ADMIN" && (
          <TabsContent value="kiosk">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl">
              <p className="text-sm text-slate-500 mb-4">Core fields required for cabin & host validation cannot be disabled.</p>
              {!kiosk ? <Loader /> : (
                <div className="space-y-1">
                  {kiosk.map((f, i) => (
                    <div key={f.key} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                      <div><span className="text-sm font-medium text-navy-900">{f.label}</span>{f.core && <span className="ml-2 text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">CORE</span>}</div>
                      <Switch checked={f.enabled} disabled={f.core} data-testid={`kiosk-${f.key}`} onCheckedChange={(v) => setKiosk(kiosk.map((x, j) => j === i ? { ...x, enabled: v } : x))} />
                    </div>
                  ))}
                  <Button onClick={saveKiosk} data-testid="save-kiosk-settings" className="bg-navy-900 mt-4">Save Kiosk Fields</Button>
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
